// src/Pages/ReceptionistDashboard/Billing.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
  Badge,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  CircularProgress,
  Avatar,
  AccessTime,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  AttachMoney as PaymentIcon,
  Create as CreateIcon,
  Done as DoneIcon,
  Error as OverdueIcon,
  CreditCard as CreditCardIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosInstance';

const Billing = () => {
  const [invoices, setInvoices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch appointments and invoices
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [appointmentsResponse, invoicesResponse] = await Promise.all([
        axiosInstance.get('/appointments/receptionist/appointments/today/'),
        axiosInstance.get('/appointments/receptionist/billing/history/')
      ]);
      
      setAppointments(appointmentsResponse.data.appointments || []);
      setInvoices(invoicesResponse.data.payments || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setNotification({
        message: 'Failed to fetch data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleGenerateInvoice = async (appointmentId) => {
    try {
      const response = await axiosInstance.post(`/appointments/receptionist/invoices/${appointmentId}/generate/`);
      
      setNotification({
        message: response.data.message,
        severity: 'success'
      });
      
      setIsInvoiceDialogOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error generating invoice:', error);
      setNotification({
        message: error.response?.data?.error || 'Failed to generate invoice',
        severity: 'error'
      });
    }
  };

  const handleProcessPayment = async (invoiceId) => {
    try {
      await axiosInstance.patch(`/appointments/receptionist/invoices/${invoiceId}/pay/`);
      
      setNotification({
        message: 'Payment processed successfully',
        severity: 'success'
      });
      
      setIsPaymentDialogOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error processing payment:', error);
      setNotification({
        message: error.response?.data?.error || 'Failed to process payment',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <OverdueIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.includes(searchTerm)
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h4' gutterBottom>
          Billing and Payments
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Generate Invoice */}
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label='Search by Patient Name or Invoice Number'
            variant='outlined'
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant='contained'
            startIcon={<CreateIcon />}
            onClick={() => setIsInvoiceDialogOpen(true)}
            sx={{ height: '56px' }}
          >
            Generate Invoice
          </Button>
        </Grid>
      </Grid>

      {/* Billing Summary Cards */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant='h6' color='primary'>
              Total Invoices
            </Typography>
            <Typography variant='h4'>{invoices.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant='h6' color='warning.main'>
              Pending Payments
            </Typography>
            <Typography variant='h4'>
              {invoices.filter(invoice => invoice.status === 'pending').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant='h6' color='success.main'>
              Completed Payments
            </Typography>
            <Typography variant='h4'>
              {invoices.filter(invoice => invoice.status === 'completed').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant='h6' color='info.main'>
              Total Revenue
            </Typography>
            <Typography variant='h4'>
              ₹{invoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'No invoices match your search criteria' : 'No invoices found'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredInvoices.map((invoice) => (
            <Grid item xs={12} key={invoice.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="h6">
                          {invoice.patient_name || 'Unknown Patient'}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(invoice.status)}
                          label={invoice.status}
                          color={getStatusColor(invoice.status)}
                          size="small"
                        />
                      </Box>
                      
                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ReceiptIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Invoice: {invoice.invoice_number || invoice.id}
                          </Typography>
                        </Box>
                                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <TimeIcon fontSize="small" color="action" />
                           <Typography variant="body2" color="text.secondary">
                             {new Date(invoice.created_at).toLocaleDateString()}
                           </Typography>
                         </Box>
                        <Typography variant="h6" color="primary">
                          ₹{invoice.amount}
                        </Typography>
                      </Stack>
                      
                      <Typography variant="body2" color="text.secondary">
                        {invoice.description || 'Consultation fee'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {invoice.status === 'pending' && (
                        <Tooltip title="Mark as Paid">
                          <IconButton
                            color="success"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setIsPaymentDialogOpen(true);
                            }}
                            size="small"
                          >
                            <DoneIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="View Details">
                        <IconButton color="info" size="small">
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Generate Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onClose={() => setIsInvoiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            Select an appointment to generate an invoice:
          </Typography>
          <List>
            {appointments
              .filter(appointment => appointment.status === 'In Room' || appointment.status === 'Completed')
              .map((appointment) => (
                <ListItem
                  key={appointment.id}
                  button
                  onClick={() => {
                    setSelectedAppointment(appointment);
                    handleGenerateInvoice(appointment.id);
                  }}
                >
                  <ListItemText
                    primary={`${appointment.patient_name} - Dr. ${appointment.doctor_name}`}
                    secondary={`${appointment.scheduled_time} - ₹${appointment.consultation_fee}`}
                  />
                  <IconButton color="primary">
                    <CreateIcon />
                  </IconButton>
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInvoiceDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog open={isPaymentDialogOpen} onClose={() => setIsPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Patient:</strong> {selectedInvoice.patient_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Invoice Number:</strong> {selectedInvoice.invoice_number || selectedInvoice.id}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Amount:</strong> ₹{selectedInvoice.amount}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Date:</strong> {new Date(selectedInvoice.created_at).toLocaleDateString()}
              </Typography>
              <Typography variant="h6" color="success.main" gutterBottom>
                Confirm that payment has been received?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleProcessPayment(selectedInvoice?.id)}
            variant="contained"
            color="success"
            startIcon={<DoneIcon />}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.severity}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Billing;
